import { useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import aboutDefence from "@/assets/about-defence.webp.asset.json";
import aboutEuParliament from "@/assets/eu-parliament.webp.asset.json";
import aboutFinance from "@/assets/about-finance.webp.asset.json";
import aboutHeaderAsset from "@/assets/about-header.jpg.asset.json";
import sectorEnergy from "@/assets/sector-energy-640.webp.asset.json";
import sectorEnergyLarge from "@/assets/sector-energy-1280.webp.asset.json";
import sectorWater from "@/assets/sector-water-640.webp.asset.json";
import sectorWaterLarge from "@/assets/sector-water-1280.webp.asset.json";
import sectorPorts from "@/assets/sector-ports-640.webp.asset.json";
import sectorPortsLarge from "@/assets/sector-ports-1280.webp.asset.json";
import sectorRail from "@/assets/sector-rail-640.webp.asset.json";
import sectorRailLarge from "@/assets/sector-rail-1280.webp.asset.json";
import sectorAirports from "@/assets/sector-airports-640.webp.asset.json";
import sectorAirportsLarge from "@/assets/sector-airports-1280.webp.asset.json";
import sectorDigital from "@/assets/sector-digital-640.webp.asset.json";
import sectorDigitalLarge from "@/assets/sector-digital-1280.webp.asset.json";
import sectorChemicals from "@/assets/sector-chemicals-pharmaceuticals-640.webp.asset.json";
import sectorChemicalsLarge from "@/assets/sector-chemicals-pharmaceuticals-1280.webp.asset.json";
import sectorSemiconductors from "@/assets/sector-semiconductors-electronics-640.webp.asset.json";
import sectorSemiconductorsLarge from "@/assets/sector-semiconductors-electronics-1280.webp.asset.json";
import sectorRawMaterials from "@/assets/sector-critical-raw-materials-640.webp.asset.json";
import sectorRawMaterialsLarge from "@/assets/sector-critical-raw-materials-1280.webp.asset.json";
import sectorAutomotive from "@/assets/sector-automotive-batteries-640.webp.asset.json";
import sectorAutomotiveLarge from "@/assets/sector-automotive-batteries-1280.webp.asset.json";
import policyDefence from "@/assets/policy-defence-640.webp.asset.json";
import policyDefenceLarge from "@/assets/policy-defence-1280.webp.asset.json";
import { Button } from "@/components/ui/button";
import { HeroFeature } from "@/components/site/HeroFeature";
import { PageHero } from "@/components/site/PageHero";
import { PhotoCard } from "@/components/site/PhotoCard";
import { LoadMoreMeetingsLink } from "@/components/site/LoadMoreButton";
import { meetingsQuery } from "@/lib/content";


const TILE_IMAGE_SIZES = "(min-width: 1400px) 322px, (min-width: 1024px) calc((100vw - 112px) / 4), (min-width: 768px) calc((100vw - 104px) / 3), (min-width: 640px) calc((100vw - 72px) / 2), calc(100vw - 40px)";

const FOCUS_AREAS = [
  { image: aboutDefence.url, srcSet: undefined, width: 650, height: 290, title: "Defence and Security" },
  { image: sectorEnergy.url, srcSet: `${sectorEnergy.url} 640w, ${sectorEnergyLarge.url} 1280w`, width: 640, height: 289, title: "Energy" },
  { image: sectorWater.url, srcSet: `${sectorWater.url} 640w, ${sectorWaterLarge.url} 1280w`, width: 640, height: 289, title: "Water" },
  { image: sectorPorts.url, srcSet: `${sectorPorts.url} 640w, ${sectorPortsLarge.url} 1280w`, width: 640, height: 289, title: "Ports and Logistics" },
  { image: sectorRail.url, srcSet: `${sectorRail.url} 640w, ${sectorRailLarge.url} 1280w`, width: 640, height: 289, title: "Rail and Roads" },
  { image: sectorAirports.url, srcSet: `${sectorAirports.url} 640w, ${sectorAirportsLarge.url} 1280w`, width: 640, height: 289, title: "Airports" },
  { image: sectorDigital.url, srcSet: `${sectorDigital.url} 640w, ${sectorDigitalLarge.url} 1280w`, width: 640, height: 289, title: "Digital Infrastructure" },
  { image: sectorChemicals.url, srcSet: `${sectorChemicals.url} 640w, ${sectorChemicalsLarge.url} 1280w`, width: 640, height: 320, title: "Chemicals and Pharmaceuticals" },
  { image: sectorSemiconductors.url, srcSet: `${sectorSemiconductors.url} 640w, ${sectorSemiconductorsLarge.url} 1280w`, width: 640, height: 320, title: "Semiconductors and Electronics" },
  { image: sectorRawMaterials.url, srcSet: `${sectorRawMaterials.url} 640w, ${sectorRawMaterialsLarge.url} 1280w`, width: 640, height: 320, title: "Critical Raw Materials" },
  { image: sectorAutomotive.url, srcSet: `${sectorAutomotive.url} 640w, ${sectorAutomotiveLarge.url} 1280w`, width: 640, height: 320, title: "Automotive and Batteries" },
];

const POLICY_AREAS = [
  { image: aboutEuParliament.url, srcSet: undefined, width: 650, height: 290, title: "EU Policy" },
  { image: aboutFinance.url, srcSet: undefined, width: 650, height: 290, title: "Global Finance" },
  { image: policyDefence.url, srcSet: `${policyDefence.url} 640w, ${policyDefenceLarge.url} 1280w`, width: 640, height: 272, title: "Defence and Security Cooperation" },
];

// Latest Meetings shows exactly two rows: the heading takes the first cell, so the
// tile count per breakpoint is 2 x columns - 1 (1 / 3 / 5 / 7 tiles).
const LATEST_TILE_VISIBILITY = [
  "",
  "hidden min-[768px]:block",
  "hidden min-[768px]:block",
  "hidden min-[1024px]:block",
  "hidden min-[1024px]:block",
  "hidden min-[1225px]:block",
  "hidden min-[1225px]:block",
];

const INITIATIVES = ["Women in Diplomacy and Government", "New Diplomats", "The Diplomatic Circle"];

const COMMITTEE = [
  {
    name: "Maximilian Hoffmann",
    role: "President",
    details: [
      "President, Foreign Council On Economic Relations (2022 – )",
      "Executive Chairman and Secretary-General, Foreign Council On Economic Relations (2019 – 2022)",
      "Deputy-Spokesperson of the Executive Board, Alliance of Associations (2020 – 2023)",
      "Secretary-General, Federal Business Associations of Germany and the Gulf Cooperation Council (2016 – 2019)",
      "Chairman, German Partner (2017 – 2018)",
      "Member of the Board of Directors, Federal Association of German Business in Ukraine (2008 – 2016)",
    ],
  },
  {
    name: "Alexander Kulitz, MdB a.D.",
    role: "Vice-President and Chairman",
    details: [
      "CEO, Foreign Council On Economic Relations GmbH (Jan 2024 – )",
      "Chairman, Foreign Council On Economic Relations (2021 – )",
      "Member, German Federal Parliament / Bundestag, FDP (2017 – 2021)",
      "Member, Foreign Affairs Committee, German Bundestag (2018 – 2021)",
      "Deputy Chairman, Parliamentary Group for Relations with the ASEAN States German Bundestag (2018 – 2021)",
      "Federal Chairman, Junior Chamber of Commerce Germany WJD (2017 – 2018)",
    ],
  },
  {
    name: "Andrej Gross",
    role: "Vice President (hon.)",
    details: ["Director, Deutsche Messe AG"],
  },
  {
    name: "Tanja Kreisz",
    role: "Vice President (hon.) Women in Diplomacy and Government",
    details: [],
  },
  {
    name: "Catherine Welling",
    role: "Co-Chair, Women in Diplomacy and Defence",
    details: ["Member of the Executive Board, constellr"],
  },
];

export const Route = createFileRoute("/about")({
  staticData: { sitemap: true },
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(meetingsQuery(10)),
  head: () => ({
    meta: [
      { title: "About the Council — Translating Government Priorities into Investments" },
      {
        name: "description",
        content:
          "International non-profit organisation connecting governments, investors and industry across infrastructure, chemicals, pharmaceuticals, semiconductors, critical raw materials, automotive and batteries.",
      },
      { property: "og:title", content: "About the Council — Translating Government Priorities into Investments" },
      {
        property: "og:description",
        content:
          "International non-profit organisation connecting governments, investors and industry across infrastructure, chemicals, pharmaceuticals, semiconductors, critical raw materials, automotive and batteries.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://foreign-council.org/about" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://foreign-council.org/about" }],
  }),
  component: AboutPage,
});

function SideHeading({ children, id }: { children: string; id?: string }) {
  return (
    <h2 id={id} className="scroll-mt-24 fc-h3 text-accent">
      {children}
      <span className="mt-2 block w-full border-b border-primary" />
    </h2>
  );
}


// Inserts a line break after `after`, but only from the desktop breakpoint up.
function DesktopBreakAfter({ text, after }: { text: string; after: string }) {
  const index = text.indexOf(after);
  if (index === -1) return <>{text}</>;
  const end = index + after.length;
  return (
    <>
      {text.slice(0, end)}
      <br className="hidden min-[1100px]:inline" />
      {text.slice(end)}
    </>
  );
}

function TabletBreakAfter({ text, after }: { text: string; after: string }) {
  const index = text.indexOf(after);
  if (index === -1) return <>{text}</>;
  const end = index + after.length;
  return (
    <>
      {text.slice(0, end)}
      <br className="hidden min-[640px]:inline min-[1366px]:hidden" />
      {text.slice(end)}
    </>
  );
}


function AboutPage() {

  const { data: meetings } = useSuspenseQuery(meetingsQuery(10));
  const [activeCommittee, setActiveCommittee] = useState(0);
  const person = COMMITTEE[activeCommittee] ?? COMMITTEE[0];
  const upcoming = meetings.find((meeting) => meeting.is_upcoming);
  const latest = meetings.filter((meeting) => !meeting.is_upcoming).slice(0, 7);

  return (
    <>
      <PageHero image={aboutHeaderAsset.url} alt="Foreign Council meeting in Berlin" />

      <main className="bg-background">
        <section className="mx-auto w-full max-w-[1400px] px-5 pt-10 pb-14 sm:pt-16 sm:pb-20 lg:px-8 lg:pt-[75px] lg:pb-[90px]">
          <div className="mx-auto max-w-[1120px] text-center">
            <h1 className="fc-h2 text-foreground">
              Translating strategic government priorities into investment opportunities.
            </h1>

            <p className="mt-7 fc-h3 text-foreground">
              <TabletBreakAfter
                text="The Foreign Council On Economic Relations is a non-profit organisation for economic diplomacy."
                after="non-profit"
              />
            </p>

            <span className="mx-auto my-4 block w-11 border-b-2 border-accent" />
            <p className="mx-auto max-w-[1040px] fc-h4 text-foreground">
              <DesktopBreakAfter
                text="We convene governments, investors, and industry to advance investment in infrastructure, critical raw materials, and strategic industries worldwide."
                after="infrastructure,"
              />
            </p>

          </div>

          <div className="mt-10 sm:mt-16 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-6 md:grid-cols-3 lg:grid-cols-4">
            <div className="col-span-1 pt-4 sm:col-span-2 md:col-span-3 lg:col-span-1 lg:pt-8"><SideHeading>Sectors</SideHeading></div>
            {FOCUS_AREAS.map((area) => (
              <div key={area.title} className="block h-[262px] border border-card-border bg-background">
                <img src={area.image} srcSet={area.srcSet} sizes={area.srcSet ? TILE_IMAGE_SIZES : undefined} alt={area.title} loading="lazy" decoding="async" width={area.width} height={area.height} className="h-[110px] w-full object-cover" />
                <h3 className="px-3 py-4 sm:px-4 sm:py-5 fc-tab break-words hyphens-auto">{area.title}</h3>
              </div>
            ))}
          </div>

          <div id="policy" className="mt-8 grid scroll-mt-24 grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-6 md:grid-cols-3 lg:grid-cols-4">
            <div className="col-span-1 pt-4 sm:col-span-2 md:col-span-3 lg:col-span-1 lg:pt-8"><SideHeading>Policy</SideHeading></div>
            {POLICY_AREAS.map((area) => (
              <div key={area.title} className="block h-[262px] border border-card-border bg-background">
                <img src={area.image} srcSet={area.srcSet} sizes={area.srcSet ? TILE_IMAGE_SIZES : undefined} alt={area.title} loading="lazy" decoding="async" width={area.width} height={area.height} className="h-[110px] w-full object-cover" />
                <h3 className="px-3 py-4 sm:px-4 sm:py-5 fc-tab break-words hyphens-auto">{area.title}</h3>
              </div>
            ))}
          </div>

          <div id="initiatives" className="mt-8 grid scroll-mt-24 grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-6 md:grid-cols-3 lg:grid-cols-4">
            <div className="col-span-1 pt-4 sm:col-span-2 md:col-span-3 lg:col-span-1 lg:pt-8"><SideHeading>Initiatives</SideHeading></div>
            {INITIATIVES.map((title) => (
              title === "New Diplomats" ? (
                <Link key={title} to="/new-diplomats" className="block h-[262px] border-2 border-dotted border-card-border p-4 sm:p-5 hover:text-accent">
                  <h3 className="fc-tab">{title}</h3>
                </Link>
              ) : (
                <div key={title} className="block h-[262px] border-2 border-dotted border-card-border p-4 sm:p-5">
                  <h3 className="fc-tab">{title}</h3>
                </div>
              )
            ))}
          </div>
        </section>

        {upcoming && <HeroFeature meeting={upcoming} showReadMore={false} />}

        {latest.length > 0 && (
          <section className="mx-auto w-full max-w-[1400px] px-5 py-12 sm:py-20 lg:px-8">
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 min-[768px]:grid-cols-2 min-[1024px]:grid-cols-3 min-[1225px]:grid-cols-4">
              <div className="col-span-1 pt-4 lg:pt-8"><SideHeading>Latest Meetings</SideHeading></div>
              {latest.map((meeting, index) => (
                <div key={meeting.id} className={LATEST_TILE_VISIBILITY[index] ?? "hidden"}>
                  <PhotoCard meeting={meeting} />
                </div>
              ))}
            </div>
            <div className="mt-12 flex justify-center">
              <LoadMoreMeetingsLink className="inline-block bg-[oklch(0.35_0_0)] px-6 py-3 fc-body text-primary-foreground transition-opacity hover:opacity-85" />
            </div>
          </section>
        )}

        <section className="mx-auto w-full max-w-[1400px] px-5 py-12 sm:py-16 lg:px-8 lg:py-20">
          <h2 className="fc-h1 text-foreground">Steering Committee</h2>
          <div className="mt-6 sm:mt-10 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
            {COMMITTEE.map((member, index) => (
              <Button
                key={member.name}
                type="button"
                variant="outline"
                aria-pressed={activeCommittee === index}
                onClick={() => setActiveCommittee(index)}
                className={`h-auto min-h-[48px] w-full sm:w-auto sm:min-w-[220px] lg:min-w-[250px] whitespace-normal rounded-none border px-5 sm:px-7 fc-tab-sub shadow-none ${
                  activeCommittee === index ? "border-accent text-accent" : "border-card-border text-foreground"
                }`}
              >
                {member.name}
              </Button>
            ))}
          </div>

          {person && (
            <div className="mt-5 max-w-[1100px] text-foreground">
              <h3 className="flex items-start gap-3 sm:gap-5 fc-h4">
                <span className="text-3xl text-accent">›</span>{person.name}
              </h3>

              <span className="mt-5 block w-5 border-b-2 border-primary" />
              <p className="mt-5 fc-body">{person.role}</p>
              {person.details.length > 0 && (
                <ul className="mt-7 fc-body">
                  {person.details.map((detail) => <li key={detail}>– {detail}</li>)}
                </ul>
              )}
            </div>
          )}
        </section>
      </main>
    </>
  );
}
